import { useState } from "react"
import {
  CircleHelp,
  CirclePlay,
  CircleSlash,
  ExternalLink,
  Eye,
  Minus,
  Package,
  Triangle,
} from "lucide-react"

import { cn } from "../../lib/utils"
import { formatBucketKey } from "../../lib/format-date"
import { formatCurrency, type ExchangeRates, formatRoas } from "../../lib/money"
import {
  type CampaignDataGate,
  type CampaignMargin,
  type CampaignMetricChange,
  type CampaignPeriod,
  type CampaignTableRow,
  type CampaignCandidateRow,
  DEFAULT_CAMPAIGN_PERIOD,
  type ProductBidReview,
  useCampaignTable,
} from "../../services/use-dashboard"
import type { MarketplaceId } from "@nova-ai/shared"
import {
  CAMPAIGN_FAMILY,
  type CampaignFamily,
  campaignFamilyLabel,
} from "@nova-ai/shared/campaign-family"
import { CampaignRoasChart } from "./campaign-roas-chart"
import { Money } from "../ui/money"
import { CampaignActionDrawer, type CampaignDrawerSubject } from "./campaign-action-drawer"

// The dashboard's campaign table: one row per ADVERTISED product, read to decide
// that product's bid — raise it, lower it, watch it, or close the line (#482).
//
// It replaced a table that merged sales and ads (#396). That one answered a P&L
// question; this one answers a campaign question, and they need different rows —
// a product nobody advertises has no bid to decide, so it is not here. The cost
// of that: nothing on this page shows per-product SALES any more.
//
// HOW A ROW IS READ. Two pairs, and both halves of each are facts about the
// product's economics — never advice:
//
//   ROAS  vs  Breakeven → does the spend pay at all?
//
// A break-even bid sat beside the live bid until it was removed; with it went the
// only reference "low CVR" had, since 3 % is excellent on one product and ruinous
// on another. If the lower-bid call starts feeling unanchored, that is what to
// bring back.
//
// There is no verdict column and no target-ROAS column, both ruled out
// deliberately (#482). The raise threshold still exists — target ROAS is
// break-even × 1/(1 − targetProfitShare) — but it is a config constant times a
// number already on the row, so it rides as the band the ROAS cell colours
// itself against rather than as a column repeating its neighbour.
//
// The first column is FROZEN and the rest scroll sideways. Columns 1–5 carry the
// whole decision and must be readable without scrolling; everything right of them
// is the context you reach for when you want to know why.

type CampaignTableSectionProps = {
  marketplace: MarketplaceId
  /** Native marketplace currency for the money figures; falls back to EUR before
   *  the dashboard payload lands. */
  currency: string
  rates: ExchangeRates | undefined
}

const PERIOD_OPTIONS: { period: CampaignPeriod; label: string }[] = [
  { period: { kind: "last-week" }, label: "Last week" },
  { period: { kind: "last-30d" }, label: "Last 30 days" },
]

export function CampaignTableSection({ marketplace, currency, rates }: CampaignTableSectionProps) {
  // The card's OWN period, deliberately not the page's (#482). Local state, like
  // the P&L card's months picker — it is a reading preference, not a route.
  const [period, setPeriod] = useState<CampaignPeriod>(DEFAULT_CAMPAIGN_PERIOD)
  // Which Redcare programme is on screen (#574). Search SPA opens because it
  // carries almost all the spend — and almost all the loss.
  const [family, setFamily] = useState<CampaignFamily | null>(CAMPAIGN_FAMILY.searchSpa)
  const table = useCampaignTable(marketplace, period)

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-card">
      <div className="flex flex-col gap-3 border-b border-border/70 px-5 py-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <span className="text-sm font-medium text-foreground">Campaign performance</span>

          <p className="mt-1 text-xs text-muted-foreground">
            One Redcare programme at a time, against the window before it. Read each row against the
            ROAS it needs to break even.
          </p>
        </div>

        <PeriodPicker value={period} onChange={setPeriod} window={table.data?.window} />
      </div>

      {table.data !== undefined && !table.data.reported ? <NoAdReportNotice /> : null}

      <CampaignRoasChart
        points={table.data?.daily ?? []}
        rows={table.data?.rows ?? []}
        window={table.data?.window}
        currency={currency}
      />

      {/* The programme filter sits directly above the rows it filters — the
          period picker in the header scopes BOTH the chart and the table, and
          stacking the two together made it look like the chart followed this one
          too. It does not, on purpose (#577). */}
      <div className="px-5 pt-4">
        <FamilyPicker value={family} onChange={setFamily} rows={table.data?.rows} />
      </div>

      <CampaignTableContent
        table={table}
        family={family}
        marketplace={marketplace}
        currency={currency}
        rates={rates}
      />

      <Legend />
    </div>
  )
}

/** Which programme tabs to offer. Two are always there; "Other" appears only
 *  when the window actually holds a campaign whose name names no programme.
 *
 *  That conditional third tab is the whole safety net of a filter with no "All":
 *  a Redcare rename would otherwise leave rows classified as nothing and
 *  reachable from neither tab. The tab showing up at all is the signal to come
 *  fix `classifyCampaignFamily`.
 *
 *  It also stays while it is the SELECTED tab, even once the rows behind it are
 *  gone — a period change must not pull the current tab out from under the
 *  reader and silently show them a different programme's numbers. */
export function campaignFamilyTabs(
  rows: CampaignTableRow[] | undefined,
  selected: CampaignFamily | null
): (CampaignFamily | null)[] {
  const needsOther = selected === null || (rows ?? []).some((row) => row.campaignFamily === null)
  return [CAMPAIGN_FAMILY.searchSpa, CAMPAIGN_FAMILY.crossSell, ...(needsOther ? [null] : [])]
}

/** What one programme tab shows.
 *
 *  A recommendation has no campaign yet, so it has no family to read — and
 *  `nova ads campaign` can only create Search SPA. Offering the same candidate
 *  under Cross-sell would offer an action that does not exist. */
export function campaignFamilySlice<Candidate>(
  rows: CampaignTableRow[],
  candidates: Candidate[],
  family: CampaignFamily | null
): { rows: CampaignTableRow[]; candidates: Candidate[] } {
  return {
    rows: rows.filter((row) => row.campaignFamily === family),
    candidates: family === CAMPAIGN_FAMILY.searchSpa ? candidates : [],
  }
}

export function FamilyPicker({
  value,
  onChange,
  rows,
}: {
  value: CampaignFamily | null
  onChange: (family: CampaignFamily | null) => void
  rows: CampaignTableRow[] | undefined
}) {
  const options = campaignFamilyTabs(rows, value)
  return (
    // A filter, not a tab set — plain pressed buttons, like the period picker
    // beside it. `role="tablist"` would promise a tabpanel, `aria-controls` and
    // arrow-key navigation that this control does not have, and a promise a
    // screen reader cannot cash is worse than no role at all.
    <div
      className="inline-flex rounded-full border border-border p-0.5"
      role="group"
      aria-label="Redcare programme"
    >
      {options.map((option) => (
        <button
          key={option ?? "other"}
          type="button"
          aria-pressed={value === option}
          onClick={() => onChange(option)}
          className={cn(
            "rounded-full px-3 py-1 text-xs transition-colors",
            value === option
              ? "bg-muted font-medium text-foreground"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          {campaignFamilyLabel(option)}
        </button>
      ))}
    </div>
  )
}

function CampaignTableContent({
  table,
  family,
  marketplace,
  currency,
  rates,
}: CampaignTableSectionProps & {
  table: ReturnType<typeof useCampaignTable>
  family: CampaignFamily | null
}) {
  const rows = table.data?.rows ?? []
  const candidates = table.data?.recommendedNewCampaigns ?? []
  if (rows.length === 0 && candidates.length === 0) {
    return (
      <div className="px-5 pb-5">
        <EmptyState loading={table.isPending} error={table.error} />
      </div>
    )
  }
  const slice = campaignFamilySlice(rows, candidates, family)
  return (
    <>
      {slice.rows.length === 0 ? (
        // Deliberately NOT the "nobody advertised" empty state: the window has
        // rows, just not in this programme, and claiming otherwise would be a
        // false statement about the marketplace. Said even when the programme
        // still has candidates below, so the tab never reads as a header-only
        // table nobody can explain.
        <div className="px-5 pb-5">
          <p className="mt-3 text-sm text-muted-foreground">
            No {campaignFamilyLabel(family)} campaign ran in this period.
          </p>
        </div>
      ) : null}

      {slice.rows.length + slice.candidates.length > 0 ? (
        <FamilyGrid
          slice={slice}
          family={family}
          table={table}
          marketplace={marketplace}
          currency={currency}
          rates={rates}
        />
      ) : null}
    </>
  )
}

/** One programme's grid, with the response's action-capability flags applied.
 *  Split from the decision above it only to keep each piece under the
 *  complexity ceiling. */
function FamilyGrid({
  slice,
  family,
  table,
  marketplace,
  currency,
  rates,
}: CampaignTableSectionProps & {
  slice: { rows: CampaignTableRow[]; candidates: CampaignCandidateRow[] }
  family: CampaignFamily | null
  table: ReturnType<typeof useCampaignTable>
}) {
  return (
    <CampaignTable
      // Keyed by programme so switching tabs REMOUNTS the grid. The open action
      // drawer is state inside it, and it would otherwise outlive the row it was
      // opened from — pointing at a campaign the new tab does not even show.
      key={family ?? "other"}
      rows={slice.rows}
      candidates={slice.candidates}
      marketplace={marketplace}
      campaignActionsEnabled={table.data?.campaignActionsEnabled ?? false}
      campaignActionsPreview={table.data?.campaignActionsPreview ?? false}
      campaignActionTimeZone={table.data?.campaignActionTimeZone ?? "UTC"}
      currency={currency}
      rates={rates}
    />
  )
}

/** The grid itself, given rows. Split from the section that fetches them so the
 *  reading contract — the colour bands, the ceilings, what a missing figure looks
 *  like — can be tested without a network or a query client. */
export function CampaignTable({
  rows,
  candidates = [],
  marketplace,
  campaignActionsEnabled = false,
  campaignActionsPreview = false,
  campaignActionTimeZone = "UTC",
  currency,
  rates,
}: {
  rows: CampaignTableRow[]
  candidates?: CampaignCandidateRow[]
  marketplace?: MarketplaceId
  campaignActionsEnabled?: boolean
  campaignActionsPreview?: boolean
  campaignActionTimeZone?: string
  currency: string
  rates: ExchangeRates | undefined
}) {
  const [selected, setSelected] = useState<CampaignDrawerSubject | null>(null)
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border/60 text-xs text-muted-foreground">
              {/* Frozen: the row is unreadable once its name scrolls away. */}
              <th className="sticky left-0 z-10 w-64 min-w-64 bg-card py-2 pl-5 pr-3 text-left font-normal">
                Product
              </th>

              <Th label="Ads spend" />

              <Th label="ROAS" title="Realised return on ad spend, net of VAT" />

              <Th label="Breakeven" title="The ROAS this product must beat to pay for itself" />

              <Th
                label="Bid"
                title="The bid charged in this window, and the latest completed analysis"
              />

              <Th label="Margin" title="Per-unit margin, net of VAT and all marketplace fees" />

              <Th label="CVR" />

              <Th label="Clicks" />

              <Th label="Impressions" />

              <Th label="CTR" />
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => (
              <Row
                key={`${row.campaignId ?? "missing"}:${row.sku}`}
                row={row}
                currency={currency}
                rates={rates}
                {...(marketplace && row.campaignId
                  ? { onOpen: () => setSelected(existingDrawerItem(row)) }
                  : {})}
              />
            ))}
          </tbody>
        </table>
      </div>

      {candidates.length > 0 ? <CandidateCampaigns candidates={candidates} /> : null}

      {selected && marketplace ? (
        <CampaignActionDrawer
          key={`${selected.subject.kind}:${selected.campaignId ?? "candidate"}:${selected.sku}`}
          marketplace={marketplace}
          campaignActionsEnabled={campaignActionsEnabled}
          campaignActionsPreview={campaignActionsPreview}
          campaignActionTimeZone={campaignActionTimeZone}
          item={selected}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </>
  )
}

/** A right-aligned header. `title` carries the full wording for the ones whose
 *  label had to be short enough to fit. */
function Th({ label, title }: { label: string; title?: string }) {
  return (
    <th className="whitespace-nowrap py-2 px-3 text-right font-normal" title={title}>
      {label}
    </th>
  )
}

function Row({
  row,
  currency,
  rates,
  onOpen,
}: {
  row: CampaignTableRow
  currency: string
  rates: ExchangeRates | undefined
  onOpen?: () => void
}) {
  // Muting is asymmetric on purpose: only a LOSING line has to buy its evidence
  // before we believe it is losing. A winner is never dimmed.
  const unproven = row.dataGate !== null && !row.dataGate.met
  const label = row.name ?? row.sku
  return (
    <tr
      className={cn(
        "group border-b border-border/40 transition-colors last:border-0 hover:bg-table-row-hover",
        onOpen &&
          "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      )}
      {...(onOpen
        ? {
            tabIndex: 0,
            "aria-label": `Review campaign action for ${label}`,
            onClick: (event: React.MouseEvent<HTMLTableRowElement>) => {
              if ((event.target as HTMLElement).closest("a,button,input")) {
                return
              }
              event.currentTarget.focus()
              onOpen()
            },
            onKeyDown: (event: React.KeyboardEvent<HTMLTableRowElement>) => {
              if ((event.target as HTMLElement).closest("a,button,input,textarea,select")) {
                return
              }
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault()
                onOpen()
              }
            },
          }
        : {})}
    >
      <ProductCell row={row} />

      <Td>
        <Stacked change={row.changes.adSpend} format={(v) => formatCurrency(v, currency)}>
          <Money
            amount={row.adSpend}
            currency={currency}
            rates={rates}
            layout="inline"
            className="font-medium text-foreground"
          />
        </Stacked>
      </Td>

      <Td>
        <Stacked change={row.changes.roas} format={formatRoas} dimmed={unproven}>
          <span
            className={cn("tabular-nums font-medium", roasTone(row), unproven && "opacity-50")}
            title={roasTitle(row, currency)}
          >
            {row.roas === null ? <Dash /> : formatRoas(row.roas)}
          </span>
        </Stacked>
      </Td>

      <Td>
        {row.breakevenRoas === null ? (
          <Dash />
        ) : (
          <span className="tabular-nums text-muted-foreground">
            {formatRoas(row.breakevenRoas)}
          </span>
        )}
      </Td>

      <BidCell row={row} currency={currency} rates={rates} />

      <Td>
        {row.margin === null ? (
          <Dash />
        ) : (
          <span className="tabular-nums text-foreground" title={marginTitle(row.margin, currency)}>
            <span>{formatCurrency(row.margin.realIncome, currency)}</span>

            <PerUnit />
          </span>
        )}
      </Td>

      <Td>
        <Stacked change={row.changes.cvrPct} format={formatPct}>
          {row.cvrPct === null ? <Dash /> : <Muted>{formatPct(row.cvrPct)}</Muted>}
        </Stacked>
      </Td>

      <Td>
        <Stacked change={row.changes.clicks} format={(v) => v.toLocaleString()}>
          <span className="tabular-nums text-muted-foreground">{row.clicks.toLocaleString()}</span>
        </Stacked>
      </Td>

      <Td>
        <Stacked change={row.changes.impressions} format={(v) => v.toLocaleString()}>
          <span className="tabular-nums text-muted-foreground">
            {row.impressions.toLocaleString()}
          </span>
        </Stacked>
      </Td>

      <Td>
        <Stacked change={row.changes.ctrPct} format={formatPct}>
          {row.ctrPct === null ? (
            <Dash />
          ) : (
            // Loud only when it is low: a weak CTR is the one case where buying a
            // better position with a higher bid is wasted, so it earns attention
            // exactly there and nowhere else.
            <span
              className={cn(
                "tabular-nums",
                row.ctrPct < 1 ? "text-warning" : "text-muted-foreground"
              )}
            >
              {formatPct(row.ctrPct)}
            </span>
          )}
        </Stacked>
      </Td>
    </tr>
  )
}

/** The bid charged inside this window, with its change beneath — and the latest
 *  completed analysis as a mark beside it.
 *
 *  A break-even ceiling (`cvr × margin`) sat under this figure until it was
 *  removed. With it went the only reference a CVR had, so nothing on the row now
 *  says whether a bid is high FOR THIS PRODUCT — only whether it moved. */
function BidCell({
  row,
  currency,
  rates,
}: {
  row: CampaignTableRow
  currency: string
  rates: ExchangeRates | undefined
}) {
  return (
    <td className="py-2 px-3 text-right">
      <Stacked change={row.changes.bid} format={(v) => formatCurrency(v, currency)}>
        <span className="inline-flex items-center gap-1">
          {row.bid === null ? (
            <Dash />
          ) : (
            <Money
              amount={row.bid}
              currency={currency}
              rates={rates}
              layout="inline"
              className="font-medium text-foreground"
            />
          )}

          {row.bidReview === null ? null : <ActionMark review={row.bidReview} />}
        </span>
      </Stacked>
    </td>
  )
}

/** Analysis decisions and the mark each wears. A
 *  bid move is red in both directions: raising and cutting are both money
 *  moving, and neither is good news on its own. */
const ACTION_MARKS = {
  "raise-bid": { Icon: Triangle, className: "text-destructive", label: "raise bid" },
  "lower-bid": { Icon: Triangle, className: "rotate-180 text-destructive", label: "lower bid" },
  hold: { Icon: Minus, className: "text-muted-foreground/60", label: "hold" },
  watch: { Icon: Eye, className: "text-muted-foreground/60", label: "watch" },
  activate: { Icon: CirclePlay, className: "text-success", label: "activate campaign" },
  pause: {
    Icon: CircleSlash,
    className: "text-muted-foreground/60",
    label: "Deactivate campaign",
  },
  "data-check": { Icon: CircleHelp, className: "text-muted-foreground/60", label: "check data" },
  retest: { Icon: Eye, className: "text-muted-foreground/60", label: "retest" },
  propose: { Icon: Triangle, className: "text-muted-foreground/60", label: "propose campaign" },
} as const satisfies Record<
  ProductBidReview["action"],
  { Icon: typeof Triangle; className: string; label: string }
>

/** The mark for an action this build does not know. `action` is a plain `text`
 *  column with no database enum behind it, so a value outside the five reaches
 *  the client whenever a newer writer lands first. Looking one up blind threw
 *  inside render and took the whole table down over one row. */
const UNKNOWN_ACTION_MARK = { Icon: CircleHelp, className: "text-muted-foreground/60" } as const

/** The analysis's last action, faded until the live bid confirms it. Redcare has no
 *  write API, so every recommendation is a person typing into the portal — a
 *  solid mark read as "done" would stop an operator checking. */
function ActionMark({ review }: { review: ProductBidReview }) {
  const mark = ACTION_MARKS[review.action] ?? UNKNOWN_ACTION_MARK
  const pending = review.recommendedBid !== null && review.applied !== true
  return (
    <span
      role="img"
      aria-label={`${actionLabel(review.action)}${pending ? ", not applied yet" : ""}`}
      className={cn("inline-flex", pending && "opacity-40")}
      title={bidReviewTitle(review)}
    >
      <mark.Icon className={cn("size-2.5 fill-current", mark.className)} />
    </span>
  )
}

function actionLabel(action: ProductBidReview["action"]): string {
  return ACTION_MARKS[action]?.label ?? action
}

function bidReviewTitle(review: ProductBidReview): string {
  const when = formatBucketKey(review.reviewDate, "day")
  const head =
    review.recommendedBid === null
      ? `analysed ${when} — ${actionLabel(review.action)}`
      : `analysed ${when} — ${actionLabel(review.action)} to ${review.recommendedBid}`
  return `${head}\n${review.reason}`
}

/** Below break-even red, above the target green, between them grey — the three
 *  bands ARE three of the four decisions, so the threshold lives in the colour
 *  rather than in a column that would repeat its neighbour. */
function roasTone(row: CampaignTableRow): string {
  if (row.roas === null || row.breakevenRoas === null) {
    return "text-foreground"
  }
  if (row.roas < row.breakevenRoas) {
    return "text-destructive"
  }
  if (row.targetRoas !== null && row.roas >= row.targetRoas) {
    return "text-success"
  }
  return "text-foreground"
}

/** Spells the band out in words, because a threshold carried only by colour is no
 *  threshold to anyone who cannot see it — and adds why a figure is dimmed. */
function roasTitle(row: CampaignTableRow, currency: string): string {
  const lines: string[] = []
  if (row.breakevenRoas !== null) {
    lines.push(`break-even ${formatRoas(row.breakevenRoas)}`)
  }
  if (row.targetRoas !== null) {
    lines.push(`raise above ${formatRoas(row.targetRoas)}`)
  }
  if (row.dataGate !== null && !row.dataGate.met) {
    lines.push(gateLine(row.dataGate, currency))
  }
  return lines.join("\n")
}

/** Why a losing row is dimmed, in the money it has actually risked. */
function gateLine(gate: CampaignDataGate, currency: string): string {
  return `spent ${formatCurrency(gate.spend, currency)} of the ${formatCurrency(
    gate.required,
    currency
  )} (2× margin) that a losing verdict needs — too thin to act on`
}

/** The margin's whole arithmetic, so the figure is checkable rather than trusted.
 *  Prices are the catalog's VAT-inclusive figures; the margin is net. */
function marginTitle(margin: CampaignMargin, currency: string): string {
  const lines = [
    margin.unitPrice === null
      ? "price unknown"
      : `price ${formatCurrency(margin.unitPrice, currency)} incl. VAT`,
    `cost ${formatCurrency(margin.unitCost, currency)}`,
    `margin ${formatCurrency(margin.realIncome, currency)} net of VAT, commission and shipping`,
    `snapshot ${formatBucketKey(margin.asOf, "day")}`,
  ]
  return lines.join("\n")
}

function PeriodPicker({
  value,
  onChange,
  window,
}: {
  value: CampaignPeriod
  onChange: (period: CampaignPeriod) => void
  window: { from: string; to: string } | undefined
}) {
  // The custom range starts from whatever window is on screen, so opening it is
  // an edit rather than a blank form.
  const [custom, setCustom] = useState<{ from: string; to: string }>(window ?? { from: "", to: "" })
  const showCustom = value.kind === "custom"
  return (
    <div className="flex flex-col items-start gap-1 sm:items-end">
      <div className="inline-flex rounded-full border border-border p-0.5">
        {PERIOD_OPTIONS.map((option) => (
          <button
            key={option.label}
            type="button"
            onClick={() => onChange(option.period)}
            className={cn(
              "rounded-full px-3 py-1 text-xs transition-colors",
              value.kind === option.period.kind
                ? "bg-muted font-medium text-foreground"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {option.label}
          </button>
        ))}

        <button
          type="button"
          onClick={() => onChange({ kind: "custom", ...(window ?? custom) })}
          className={cn(
            "rounded-full px-3 py-1 text-xs transition-colors",
            showCustom
              ? "bg-muted font-medium text-foreground"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          Custom
        </button>
      </div>

      {showCustom ? (
        // Two plain date inputs, and the SERVER validates the range — it already
        // refuses an inverted or overlong one for every other dashboard read, and
        // a second rule here could disagree with it.
        <div className="flex items-center gap-1.5 text-xs">
          <input
            type="date"
            aria-label="From"
            value={custom.from}
            onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))}
            className="rounded-md border border-border bg-card px-2 py-1"
          />

          <span className="text-muted-foreground">–</span>

          <input
            type="date"
            aria-label="To"
            value={custom.to}
            onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))}
            className="rounded-md border border-border bg-card px-2 py-1"
          />

          <button
            type="button"
            disabled={!custom.from || !custom.to}
            onClick={() => onChange({ kind: "custom", from: custom.from, to: custom.to })}
            className="rounded-md border border-border px-2 py-1 font-medium text-foreground disabled:opacity-40"
          >
            Apply
          </button>
        </div>
      ) : null}

      {/* Said in words, not just implied by a highlighted pill: this card carries
          its OWN period, and two different windows on one page is a real trap. */}
      <span className="text-xs tabular-nums text-muted-foreground">
        {window === undefined
          ? " "
          : `${formatBucketKey(window.from, "day")} – ${formatBucketKey(window.to, "day")}`}
      </span>
    </div>
  )
}

/** What the colours mean, once, above the rows — a band carried only by colour is
 *  unreadable without it. */
function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border/70 px-5 py-2.5 text-xs text-muted-foreground">
      <Swatch className="text-destructive" label="ROAS below break-even — losing money" />

      <Swatch className="text-foreground" label="pays for itself" />

      <Swatch
        className="text-success"
        label="clears the target — more spend is worth considering"
      />

      <span className="opacity-50">dimmed — too little spent to judge</span>
    </div>
  )
}

/** One band of the ROAS colouring, named. A threshold carried only by colour is
 *  no threshold to anyone who cannot see it. */
function Swatch({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn("size-2 rounded-full bg-current", className)} />

      <span>{label}</span>
    </span>
  )
}

function ProductCell({ row }: { row: CampaignTableRow }) {
  const label = row.name ?? row.sku
  const body = (
    <>
      <ProductThumb url={row.imageUrl} alt={label} />

      <div className="min-w-0">
        <div className="flex items-center gap-1 truncate font-medium text-foreground" title={label}>
          <span className="truncate">{label}</span>

          {row.productUrl ? (
            <ExternalLink className="size-3 shrink-0 text-muted-foreground/50 transition-colors group-hover:text-foreground" />
          ) : null}
        </div>

        <div className="truncate text-xs text-muted-foreground">{row.sku}</div>
      </div>
    </>
  )
  return (
    <td className="sticky left-0 z-10 w-64 min-w-64 bg-card py-2 pl-5 pr-3 group-hover:bg-table-row-hover">
      <div className="flex items-center gap-2">
        {row.productUrl ? (
          <a
            href={row.productUrl}
            target="_blank"
            rel="noreferrer"
            className="flex min-w-0 flex-1 items-center gap-3"
            onClick={(event) => event.stopPropagation()}
          >
            {body}
          </a>
        ) : (
          <div className="flex min-w-0 flex-1 items-center gap-3">{body}</div>
        )}
      </div>
    </td>
  )
}

function CandidateCampaigns({ candidates }: { candidates: CampaignCandidateRow[] }) {
  return (
    <section
      className="border-t border-border/70 px-5 py-4"
      aria-labelledby="recommended-campaigns"
    >
      <h3 id="recommended-campaigns" className="text-sm font-medium text-foreground">
        Recommended new campaigns
      </h3>

      <p className="mt-1 text-xs text-muted-foreground">
        Recommendations only. New campaigns cannot be created from this page.
      </p>

      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {candidates.map((candidate) => (
          <div
            key={candidate.sku}
            className="flex items-center gap-3 rounded-lg border border-border bg-card p-3 text-left"
          >
            <ProductThumb url={candidate.imageUrl} alt={candidate.name ?? candidate.sku} />

            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-foreground">
                {candidate.name ?? candidate.sku}
              </span>

              <span className="block text-xs text-muted-foreground">{candidate.sku}</span>
            </span>

            <span className="text-xs capitalize text-muted-foreground">
              {candidate.analysis.priority}
            </span>
          </div>
        ))}
      </div>
    </section>
  )
}

function existingDrawerItem(row: CampaignTableRow): CampaignDrawerSubject {
  return {
    subject: {
      kind: "existing-campaign",
      campaignId: row.campaignId ?? "",
      productId: row.sku,
    },
    name: row.name ?? row.sku,
    sku: row.sku,
    imageUrl: row.imageUrl,
    productUrl: row.productUrl,
    campaignName: row.campaignName ?? row.bidReview?.campaignName ?? null,
    campaignId: row.campaignId,
    ean: row.bidReview?.analysis?.ean ?? null,
    analysis: row.bidReview?.analysis ?? null,
  }
}

function ProductThumb({ url, alt }: { url: string | null; alt: string }) {
  if (!url) {
    return (
      <div className="flex size-8 shrink-0 items-center justify-center rounded-md border border-border/60 bg-muted/40 text-muted-foreground/40">
        <Package className="size-4" />
      </div>
    )
  }
  return (
    <img
      src={url}
      alt={alt}
      loading="lazy"
      decoding="async"
      className="size-8 shrink-0 rounded-md border border-border/60 bg-white object-contain"
    />
  )
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="whitespace-nowrap py-2 px-3 text-right">{children}</td>
}

function Dash() {
  return <span className="tabular-nums text-muted-foreground/40">—</span>
}

function Muted({ children }: { children: React.ReactNode }) {
  return <span className="tabular-nums text-muted-foreground">{children}</span>
}

/** Marks a figure as PER UNIT in a row whose other money is a period total. */
function PerUnit() {
  return <span className="text-xs text-muted-foreground/70">/u</span>
}

/** A figure with its change against the previous window beneath it — the shape
 *  `Bid / max` already uses, now carrying the trend instead of a second column.
 *
 *  One Δ column could only ever describe ONE metric, and a reader had to remember
 *  which; under the number, each change belongs to the figure above it and needs
 *  no label at all. */
function Stacked({
  change,
  format,
  dimmed,
  children,
}: {
  change: CampaignMetricChange | null
  /** Renders the previous value for the hover, in that metric's own units. */
  format: (value: number) => string
  dimmed?: boolean | undefined
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col items-end tabular-nums">
      {children}

      <ChangeLine change={change} format={format} dimmed={dimmed} />
    </div>
  )
}

function ChangeLine({
  change,
  format,
  dimmed,
}: {
  change: CampaignMetricChange | null
  format: (value: number) => string
  dimmed?: boolean | undefined
}) {
  if (change === null) {
    // Absent from the previous window is not "did not move" — an empty line says
    // so without spending a word on it.
    return <span className="text-xs text-muted-foreground/40">·</span>
  }
  const title = `was ${format(change.previous)} in the window before`
  if (change.changePct === null) {
    // A rise from zero is not a percentage. The baseline is still worth stating.
    return (
      <span className="text-xs text-muted-foreground/60" title={title}>
        from {format(change.previous)}
      </span>
    )
  }
  return (
    <span
      className={cn("text-xs", deltaTone(change.changePct), dimmed && "opacity-50")}
      title={title}
    >
      {formatChangePct(change.changePct)}
    </span>
  )
}

/** Always signed, so a change never reads as a level. */
function formatChangePct(changePct: number): string {
  const formatted = Math.abs(changePct).toLocaleString(undefined, { maximumFractionDigits: 1 })
  if (changePct > 0) {
    return `+${formatted}%`
  }
  if (changePct < 0) {
    return `−${formatted}%`
  }
  return "0%"
}

function deltaTone(change: number): string {
  if (change > 0) {
    return "text-success"
  }
  if (change < 0) {
    return "text-destructive"
  }
  return "text-muted-foreground"
}

function formatPct(value: number): string {
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`
}

/** A window with no imported advertiser report must SAY so — blank columns would
 *  otherwise read as "we advertised nothing", which is a different fact. */
function NoAdReportNotice() {
  return (
    <p className="border-b border-border/40 px-5 py-2.5 text-xs text-muted-foreground">
      No advertiser report imported for this period — every figure below is unknown, not zero.
    </p>
  )
}

/** Three different states that all show no rows, and they must not read alike.
 *
 *  The failed READ is the one that matters: "no product was advertised in this
 *  period" is a claim about the marketplace, and printing it when the request
 *  never succeeded reports a refusal as a fact. That is exactly what happened —
 *  an unclamped "this week" asked for a window ending in the future, the API
 *  refused it, and the card said nobody advertised anything. */
export function EmptyState({ loading, error }: { loading: boolean; error: Error | null }) {
  if (loading) {
    return <p className="mt-3 text-sm text-muted-foreground">Loading campaigns…</p>
  }
  if (error !== null) {
    return (
      <p className="mt-3 text-sm text-destructive">
        This period could not be read: {error.message}
      </p>
    )
  }
  return (
    <p className="mt-3 text-sm text-muted-foreground">No product was advertised in this period.</p>
  )
}
